import { Component, ElementRef, Output, EventEmitter, Input, OnChanges, SimpleChanges, HostListener, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { ItemNode } from '../models/item';
import { MaterialTree } from '../models/material-tree';
import { TreeUtils } from '../tree/tree-utils';
import { NodeSelectionHandler } from '../tree/node-selection-handler';
import { TreeCommandsHandler } from '../tree/tree-commands-handler';
import { TreeFilterHandler } from '../tree/tree-filter-handler';
import { TreeBuilder } from '../tree/tree-builder';
import { differenceWith, isEmpty } from 'lodash-es';

export type SelectorStatus = 'view' | 'edit';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-items-selector',
    templateUrl: './items-selector.component.html',
    styleUrls: ['./items-selector.component.scss'],
    standalone: false
})
export class ItemsSelectorComponent implements OnChanges, OnDestroy {
    @Input() disabled: boolean;
    @Input() emitItemsById: boolean;
    @Input() itemList;
    @Input() selectedItems: ItemNode[];
    @Input() visibleSelectAllButton;
    @Input() title;
    @Input() noItemsSelectedMessage: string;
    @Input() inputSearchText: string;
    @Input() status: SelectorStatus;
    @Input() filter: string;
    @Input() autoSelectParent: boolean;

    @Output() filterChange = new EventEmitter<string>();
    @Output() loading = new EventEmitter();
    @Output() maxSelectableItemsNotify = new EventEmitter();
    @Output() notSelectableItemsNotify = new EventEmitter();
    @Output() selectedItemsChange = new EventEmitter<ItemNode[]>();// async true
    @Output() statusChange = new EventEmitter();
    @Output() isEmpty = new EventEmitter<boolean>();

    treeBuilder: TreeBuilder = new TreeBuilder();
    materialTree: MaterialTree;
    treeUtils: TreeUtils;
    nodeSelectionHandler: NodeSelectionHandler;
    treeCommandsHandler: TreeCommandsHandler;
    treeFilterHandler: TreeFilterHandler;
    nodesSelectedCounts = {};
    focusOnFilter: boolean = false;

    private treeSelectedItemsSubscription: Subscription;
    private filterChangeSubscription: Subscription;

    constructor(public elementRef: ElementRef) { }

    private initComponent() {
        this.materialTree = this.treeBuilder.buildTree(this.itemList);
        this.treeUtils = new TreeUtils(this.materialTree);
        this.nodeSelectionHandler = new NodeSelectionHandler(this.materialTree, this.treeUtils, this.maxSelectableItemsNotify, this.notSelectableItemsNotify, this.autoSelectParent);
        this.treeCommandsHandler = new TreeCommandsHandler(this.materialTree, this.treeUtils);
        this.treeFilterHandler = new TreeFilterHandler(this.materialTree, this.treeUtils, this.itemList);

        if (this.treeSelectedItemsSubscription) {
            this.treeSelectedItemsSubscription.unsubscribe();
        }
        this.treeSelectedItemsSubscription = this.treeUtils.selectedItemsChange.subscribe(items => {
            if (!this.areItemNodesArraysEqual(this.selectedItems ?? [], items ?? [])) {
                this.onSelectedItemsChange(items);
            }
        });

        if (this.filterChangeSubscription) {
            this.filterChangeSubscription.unsubscribe();
        }
        this.filterChangeSubscription = this.treeFilterHandler.filter$.subscribe((filter) => this.filterChange.emit(filter));

        if (this.selectedItems) {
            this.treeUtils.refreshSelectedItems(this.selectedItems);
        }
    }

    ngOnChanges({ filter, itemList, selectedItems, status }: SimpleChanges) {
        if (itemList) {
            this.initComponent();
        }
        if (status) {
            if (status.currentValue === 'edit') {
                this.setEditStatus();
            } else {
                this.setViewStatus()
            }
        }
        if (selectedItems) {
            this.onSelectedItemsChange(selectedItems.currentValue);
        }

        this.treeUtils.resetChildrenSelectedCount();
    }

    ngOnDestroy() {
        if (this.treeSelectedItemsSubscription) {
            this.treeSelectedItemsSubscription.unsubscribe();
        }
        if (this.filterChangeSubscription) {
            this.filterChangeSubscription.unsubscribe();
        }
    }

    onSelectedItemsChange(selectedItems: ItemNode[] | undefined) {
        if (!selectedItems) {
            selectedItems = [];
        }
        this.selectedItems = selectedItems;
        this.isEmpty.emit(!this.selectedItems.length);
        this.selectedItemsChange.emit(selectedItems);
        if (this.treeUtils) {
            this.treeUtils.refreshSelectedItems(selectedItems);

            if (this.status === 'view') {
                this.setViewStatus();
            }
        }
    }

    updateFilterFocusState(hasFocus: boolean) {
        this.focusOnFilter = hasFocus;
    }

    // ### Inizio gestione modalità VIEW/EDIT ### //
    public setViewStatus() {
        this.treeFilterHandler.filterValue = '';
        const hasSelectedItems = this.materialTree.checklistSelection.selected.length;
        if (hasSelectedItems) {
            const selectedItems = this.materialTree.checklistSelection.selected.map((s => s.Id));
            const matches = this.treeFilterHandler.filterSelected(this.itemList, selectedItems);
            this.treeFilterHandler.filterSelectedItems = matches;
            const matchIds = this.treeFilterHandler.getAllIdValueFromObject(matches);
            this.materialTree.treeControl.dataNodes.forEach(node => {
                node.visible = (matchIds.indexOf(node.Id) > -1);
            });
            this.treeCommandsHandler.expandAll();
        }
        this.statusChange.emit(false);
    }

    public setEditStatus() {
        this.treeFilterHandler.filterSelectedItems = [];
        this.materialTree.treeControl.dataNodes.forEach(x => x.visible = true);
        if (this.treeFilterHandler.filterValue !== '') {
            this.treeFilterHandler.applyFilter();
        }
        else {
            this.treeCommandsHandler.collapseAll();
        }
        this.treeUtils.expandAllGroupsWithSelection();
        this.statusChange.emit(true);
    }
    // ### Fine gestione Focus ### //

    onViewNodeClick() {
        if (!this.disabled) {
            this.setEditStatus();
            this.status = 'edit';
        }
    }

    getKeyCode(event: KeyboardEvent) {
        let code = '';
        if (event.code !== undefined) {
            code = event.code;
        } else if (event.key !== undefined) {
            code = event.key;
        }
        return code;
    }

    @HostListener('window:keydown', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (this.status === 'edit') {
            let preventDefault = false;
            const key = this.getKeyCode(event);
            if (['ArrowDown', 'Numpad2', 'Down', '40', '98'].includes(key)) {
                this.nodeSelectionHandler.highlightNext();
                preventDefault = true;
            } else if (['ArrowUp', 'Numpad8', 'Up', '38', '104'].includes(key)) {
                this.nodeSelectionHandler.highlightPrevious();
                preventDefault = true;
            } else if (['ArrowRight', 'Numpad6', 'Right', '39', '102'].includes(key)) {
                this.nodeSelectionHandler.highlightChild();
                preventDefault = true;
            } else if (['ArrowLeft', 'Numpad4', 'Left', '37', '100'].includes(key)) {
                this.nodeSelectionHandler.highlightParent();
                preventDefault = true;
            } else if (['Space', '32'].includes(key)) {
                if (!this.focusOnFilter) {
                    this.nodeSelectionHandler.toggleHighlightedNode();
                    preventDefault = true;
                }
            } else if (['Escape', '27'].includes(key)) {
                this.statusChange.emit(false);
            }
            if (preventDefault) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    private areItemNodesArraysEqual(arr1: ItemNode[], arr2: ItemNode[]) {
        if (arr1 === arr2) {
            return true;
        }

        if (arr1.length !== arr2.length) {
            return false;
        }

        return isEmpty(differenceWith(arr1, arr2, (a, b) => a.Id === b.Id));
    }
}
